import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  CheckCircle2,
  ChevronLeft,
  FileSearch,
  FileText,
  Layers,
  Package,
  Receipt,
  ScanLine,
  Sparkles,
  Upload,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useNotice } from '@/components/ConfirmDialog';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { DocumentKind } from '@/db/types';
import {
  DOCUMENT_KIND_LABEL,
  addDocument,
  composeScanDocMeta,
  guessDocKindFromFilename,
} from '@/lib/documents';
import { addExpense } from '@/lib/expenses';
import { isImageMime, persistPickedFile, readUriAsBase64 } from '@/lib/files';
import { addInventoryItem } from '@/lib/inventory';
import { hapticSuccess } from '@/lib/haptics';
import { goBackOrTo } from '@/lib/nav';
import { extractDocxText, isDocxLike } from '@/lib/docx-text';
import {
  classifyPaidStatus,
  classifySmartScan,
  composeScanNotes,
  composeScanTitle,
  parseReceiptText,
  scanPdfBase64,
  scanReceiptBase64,
  type ParsedReceipt,
} from '@/lib/ocr';
import { addQuote } from '@/lib/quotes';
import { smartScanCache, takeSharedFiles, type SmartScanFormKind } from '@/lib/smart-scan';
import { getActiveProperty } from '@/lib/tasks';
import { guessExpenseCategory } from '@/lib/vendor-category';

/**
 * Smart Scan — one entry point for any piece of home paper.
 *
 * Photos: Vision reads the text, then we classify —
 *   estimate / bid / proposal → Quote form (pre-filled)
 *   receipt / invoice / bill  → Expense form (pre-filled)
 *   model/serial plate        → Inventory form (pre-filled)
 *   manual / warranty / insurance / permit / inspection
 *                             → saved straight into Media & Files
 *   unclassifiable            → the user picks the destination
 *
 * PDFs / documents (Wave 49): no OCR (Vision's image endpoint doesn't
 * read PDFs) — classified by filename keywords and saved into Media &
 * Files under the guessed kind, defaulting to Other.
 *
 * Batch mode (Wave 52): multi-select any mix of PDFs, images, and
 * documents — each is OCR'd where possible, classified, and turned
 * straight into the right record (quote / expense / inventory item,
 * with the file attached) or filed into Media & Files. A results
 * summary shows where everything went; every record stays editable.
 */

/** Doc kinds where a detected dollar amount really is the file's cost. */
const MONEY_DOC_KINDS: ReadonlySet<string> = new Set(['receipt', 'invoice', 'estimate']);

type BatchOutcome = {
  name: string;
  ok: boolean;
  where: 'quote' | 'expense' | 'inventory' | 'files' | 'error';
  detail: string;
};

export default function SmartScan() {
  const router = useRouter();
  const db = useSQLiteContext();
  const notice = useNotice();
  const [scanning, setScanning] = useState(false);
  // Set when classification needs a human — shows the destination
  // chooser with the photo attached.
  const [pendingUri, setPendingUri] = useState<string | null>(null);
  const [pendingParsed, setPendingParsed] = useState<ParsedReceipt | null>(null);
  // Batch mode (Wave 52): many files at once, records created directly.
  const [batchProgress, setBatchProgress] = useState<{
    done: number;
    total: number;
    current: string;
  } | null>(null);
  const [batchResults, setBatchResults] = useState<BatchOutcome[] | null>(null);

  const routeToForm = (
    kind: SmartScanFormKind,
    uri: string,
    parsed: ParsedReceipt | null,
    mime: string | null = 'image/jpeg',
    name: string | null = null
  ) => {
    hapticSuccess();
    smartScanCache.value = { kind, uri, parsed, mime, name };
    const dest =
      kind === 'quote' ? '/quotes/new' : kind === 'inventory' ? '/inventory/new' : '/expenses/new';
    router.replace(dest as never);
  };

  /** Persist a scanned image / picked file into Media & Files. */
  const saveToFiles = async (
    uri: string,
    kind: DocumentKind,
    title: string,
    mime: string | null,
    meta?: { vendor?: string | null; cost_cents?: number | null; notes?: string | null }
  ) => {
    const prop = await getActiveProperty(db);
    if (!prop) {
      void notice({
        title: 'No home set up yet',
        message: 'Finish onboarding first — files attach to a home.',
        tone: 'warning',
      });
      return false;
    }
    try {
      const ext = uri.match(/\.[a-zA-Z0-9]+$/)?.[0] ?? (mime?.includes('pdf') ? '.pdf' : '.jpg');
      const fileName = `scan_${Date.now()}_${Math.random().toString(36).slice(2, 8)}${ext}`;
      const fileUri = await persistPickedFile(uri, fileName);
      await addDocument(db, {
        propertyId: prop.id,
        kind,
        title: title.slice(0, 80),
        file_uri: fileUri,
        mime_type: mime ?? 'image/jpeg',
        vendor: meta?.vendor ?? null,
        cost_cents: meta?.cost_cents ?? null,
        notes: meta?.notes ?? null,
      });
      return true;
    } catch (err) {
      void notice({ title: "Couldn't save the file", message: String(err), tone: 'error' });
      return false;
    }
  };

  const KIND_LABEL: Record<string, string> = {
    manual: 'a manual',
    warranty: 'a warranty',
    insurance: 'an insurance document',
    permit: 'a permit',
    inspection: 'an inspection report',
    photo: 'a photo',
  };

  const fileAsDocument = async (
    uri: string,
    kind: DocumentKind,
    parsed: ParsedReceipt | null,
    mime: string | null,
    fallbackTitle: string
  ) => {
    const m = composeScanDocMeta(parsed, fallbackTitle);
    // Record kinds (insurance, warranty, manual…) aren't purchases —
    // a coverage limit or parts price scraped off the page is not the
    // document's "cost" (Wave 53.1: a declarations page showed
    // $500,000 on its Files tile).
    if (!MONEY_DOC_KINDS.has(kind)) m.cost_cents = null;
    const ok = await saveToFiles(uri, kind, m.title, mime, m);
    if (ok) {
      hapticSuccess();
      void notice({
        title: 'Filed it for you',
        message: `Saved as ${KIND_LABEL[kind] ?? kind} in Media & Files. Long-press it there to re-file.`,
        tone: 'info',
      });
      router.replace('/media' as never);
    }
  };

  const runScan = async (source: 'camera' | 'library') => {
    let asset: ImagePicker.ImagePickerAsset | undefined;
    try {
      if (source === 'camera') {
        const perm = await ImagePicker.requestCameraPermissionsAsync();
        if (!perm.granted) return;
        const res = await ImagePicker.launchCameraAsync({ quality: 0.7, base64: true });
        if (res.canceled || !res.assets[0]) return;
        asset = res.assets[0];
      } else {
        const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (!perm.granted) return;
        const res = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          quality: 0.7,
          base64: true,
        });
        if (res.canceled || !res.assets[0]) return;
        asset = res.assets[0];
      }
    } catch (err) {
      void notice({ title: 'Picker failed', message: String(err), tone: 'error' });
      return;
    }

    const base64 = asset?.base64 ?? null;
    if (!base64 || !asset) {
      void notice({
        title: "Couldn't read photo",
        message: 'Your photo library returned no image data. Try a different file.',
        tone: 'error',
      });
      return;
    }

    setScanning(true);
    try {
      const result = await scanReceiptBase64(base64);
      if (!result.ok) {
        // OCR failed — the photo is still worth filing. Let the user
        // pick where it goes.
        setPendingUri(asset.uri);
        setPendingParsed(null);
        if (result.error.kind !== 'no-key') {
          void notice({
            title: "Couldn't read the text",
            message: `${result.error.message} You can still file the photo — pick where below.`,
            tone: 'warning',
          });
        }
        return;
      }
      const kind = classifySmartScan(result.parsed);
      if (kind === 'expense' || kind === 'quote' || kind === 'inventory') {
        routeToForm(kind, asset.uri, result.parsed);
      } else if (kind) {
        // Paper-record kind — file it directly.
        await fileAsDocument(asset.uri, kind, result.parsed, 'image/jpeg', 'Scanned document');
      } else {
        setPendingUri(asset.uri);
        setPendingParsed(result.parsed);
      }
    } finally {
      setScanning(false);
    }
  };

  const pickScanSource = () => {
    if (Platform.OS === 'web') {
      // Web (Wave 53): one unified picker — pickDocument OCRs images
      // now too, so the old photo/document split was two buttons doing
      // the same thing.
      void pickDocument();
      return;
    }
    void runScan('camera');
  };

  /** PDF / arbitrary document path — filename-keyword classification. */
  const pickDocument = async () => {
    let res: DocumentPicker.DocumentPickerResult;
    try {
      res = await DocumentPicker.getDocumentAsync({ copyToCacheDirectory: true });
    } catch (err) {
      void notice({ title: 'Picker failed', message: String(err), tone: 'error' });
      return;
    }
    if (res.canceled || !res.assets[0]) return;
    const file = res.assets[0];
    const nameKind = guessDocKindFromFilename(file.name);
    const mime = file.mimeType ?? (file.name?.toLowerCase().endsWith('.pdf') ? 'application/pdf' : null);
    const title = file.name?.replace(/\.[a-zA-Z0-9]+$/, '') || 'Document';

    // PDFs get REAL OCR (Wave 51) via Vision files:annotate; images
    // picked through this path get image OCR too (Wave 53 — "upload a
    // PDF / document" was silently skipping OCR for photos, so nothing
    // pre-filled). Cap at ~10MB; bigger files fall back to
    // filename-based filing.
    const isPdf = mime?.includes('pdf') ?? false;
    const isImage = isImageMime(mime) || /\.(jpe?g|png|webp|heic|gif|avif|tiff?|bmp)$/i.test(file.name ?? '');
    const isDocx = isDocxLike(mime, file.name);
    const smallEnough = (file.size ?? 0) <= 10 * 1024 * 1024;
    if ((isPdf || isImage || isDocx) && smallEnough) {
      setScanning(true);
      try {
        const base64 = await readUriAsBase64(file.uri);
        if (base64) {
          // Word docs parse locally (Wave 56) — no Vision, no quota.
          const result = isDocx
            ? (() => {
                const text = extractDocxText(base64);
                return text
                  ? ({ ok: true, parsed: parseReceiptText(text) } as const)
                  : ({ ok: false, error: { kind: 'unreadable', message: 'not a readable docx' } } as const);
              })()
            : isPdf
            ? await scanPdfBase64(base64)
            : await scanReceiptBase64(base64);
          if (result.ok) {
            const kind = classifySmartScan(result.parsed);
            if (kind === 'expense' || kind === 'quote' || kind === 'inventory') {
              routeToForm(kind, file.uri, result.parsed, mime, file.name);
              return;
            }
            if (kind) {
              await fileAsDocument(file.uri, kind, result.parsed, mime, title);
              return;
            }
            // OCR read it but couldn't classify — let the filename
            // break the tie, carrying the parse along for prefill.
            if (nameKind === 'estimate') {
              routeToForm('quote', file.uri, result.parsed, mime, file.name);
              return;
            }
            if (nameKind === 'invoice' || nameKind === 'receipt') {
              routeToForm('expense', file.uri, result.parsed, mime, file.name);
              return;
            }
            // Image with readable text but no verdict from content or
            // filename — same human chooser the photo path uses.
            if (isImage) {
              setPendingUri(file.uri);
              setPendingParsed(result.parsed);
              return;
            }
          }
        }
      } finally {
        setScanning(false);
      }
    }

    // No OCR available (non-PDF document, oversized, or Vision failed):
    // money-named files still open the right form (blank + attached),
    // everything else files by name.
    if (nameKind === 'estimate') {
      routeToForm('quote', file.uri, null, mime, file.name);
      return;
    }
    if (nameKind === 'invoice' || nameKind === 'receipt') {
      routeToForm('expense', file.uri, null, mime, file.name);
      return;
    }

    const ok = await saveToFiles(file.uri, nameKind, title, mime);
    if (ok) {
      void notice({
        title: 'Filed it for you',
        message: `Saved to Media & Files as ${DOCUMENT_KIND_LABEL[nameKind].toLowerCase()}. Long-press it there to re-file.`,
        tone: 'info',
      });
      router.replace('/media' as never);
    }
  };

  /** Persist a file and register it in the document library, returning
   *  the document id so batch records can attach it. */
  const fileToLibrary = async (
    propertyId: string,
    uri: string,
    kind: DocumentKind,
    title: string,
    mime: string | null,
    meta?: { vendor?: string | null; cost_cents?: number | null; notes?: string | null }
  ): Promise<string> => {
    const ext = uri.match(/\.[a-zA-Z0-9]+$/)?.[0] ?? (mime?.includes('pdf') ? '.pdf' : '.jpg');
    const fileName = `scan_${Date.now()}_${Math.random().toString(36).slice(2, 8)}${ext}`;
    const fileUri = await persistPickedFile(uri, fileName);
    return addDocument(db, {
      propertyId,
      kind,
      title: title.slice(0, 80),
      file_uri: fileUri,
      mime_type: mime ?? 'image/jpeg',
      vendor: meta?.vendor ?? null,
      cost_cents: meta?.cost_cents ?? null,
      notes: meta?.notes ?? null,
    });
  };

  /** Classify one batch file and create the matching record directly. */
  const batchOne = async (
    propertyId: string,
    f: { uri: string; mimeType?: string | null; size?: number | null },
    name: string
  ): Promise<BatchOutcome> => {
    const mime =
      f.mimeType ?? (name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : null);
    const title = name.replace(/\.[a-zA-Z0-9]+$/, '') || 'Document';
    const isPdf = mime?.includes('pdf') ?? false;
    const isImage = isImageMime(mime) || /\.(jpe?g|png|webp|heic|gif|avif|tiff?|bmp)$/i.test(name);
    const smallEnough = (f.size ?? 0) <= 10 * 1024 * 1024;

    let parsed: ParsedReceipt | null = null;
    if ((isPdf || isImage) && smallEnough) {
      const base64 = await readUriAsBase64(f.uri);
      if (base64) {
        const result = isPdf ? await scanPdfBase64(base64) : await scanReceiptBase64(base64);
        if (result.ok) parsed = result.parsed;
      }
    } else if (isDocxLike(mime, name) && smallEnough) {
      const base64 = await readUriAsBase64(f.uri);
      const text = base64 ? extractDocxText(base64) : null;
      if (text) parsed = parseReceiptText(text);
    }

    const nameKind = guessDocKindFromFilename(name);
    let kind = parsed ? classifySmartScan(parsed) : null;
    // Same filename tie-break ladder as the single-file flow.
    if (!kind) {
      if (nameKind === 'estimate') kind = 'quote';
      else if (nameKind === 'invoice' || nameKind === 'receipt') kind = 'expense';
    }

    const vendor = parsed?.vendor?.trim() || null;
    const m = composeScanDocMeta(parsed, title);
    const label = m.title;

    if (kind === 'quote') {
      const docId = await fileToLibrary(propertyId, f.uri, 'estimate', label, mime, m);
      if (parsed?.amount_cents != null) {
        await addQuote(db, {
          propertyId,
          category: guessExpenseCategory(vendor) ?? 'Maintenance',
          vendor,
          project: parsed.project?.trim() || null,
          description: parsed.description?.trim() || null,
          amount_cents: parsed.amount_cents,
          quoted_on: parsed.date ?? Date.now(),
          valid_until: parsed.valid_until ?? null,
          notes: composeScanNotes(parsed),
          quote_doc_id: docId,
        });
        return {
          name, ok: true, where: 'quote',
          detail: `Quote — ${label}, $${(parsed.amount_cents / 100).toFixed(2)}`,
        };
      }
      return {
        name, ok: true, where: 'files',
        detail: 'Filed as an estimate — no total found, so no quote record',
      };
    }

    if (kind === 'expense') {
      const docKind: DocumentKind = parsed
        ? classifyPaidStatus(parsed)
        : nameKind === 'receipt' ? 'receipt' : 'invoice';
      const docId = await fileToLibrary(propertyId, f.uri, docKind, label, mime, m);
      if (parsed?.amount_cents != null) {
        await addExpense(db, {
          propertyId,
          category: guessExpenseCategory(vendor) ?? 'Maintenance',
          vendor,
          amount_cents: parsed.amount_cents,
          paid_on: parsed.date ?? Date.now(),
          description: composeScanTitle(parsed, docKind, label),
          notes: composeScanNotes(parsed),
          receipt_doc_id: docId,
        });
        return {
          name, ok: true, where: 'expense',
          detail: `Expense — ${label}, $${(parsed.amount_cents / 100).toFixed(2)}`,
        };
      }
      return {
        name, ok: true, where: 'files',
        detail: `Filed as ${DOCUMENT_KIND_LABEL[docKind].toLowerCase()} — no total found, so no expense record`,
      };
    }

    if (kind === 'inventory') {
      let photoUri: string | null = null;
      if (isImage) {
        const fileName = `scan_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.jpg`;
        photoUri = await persistPickedFile(f.uri, fileName);
      } else {
        await fileToLibrary(propertyId, f.uri, 'other', label, mime, m);
      }
      await addInventoryItem(db, {
        propertyId,
        name: (label || 'Scanned item').slice(0, 80),
        brand: vendor,
        model: parsed?.model ?? null,
        serial_number: parsed?.serial ?? null,
        photo_uri: photoUri,
      });
      return {
        name, ok: true, where: 'inventory',
        detail: `Inventory — ${label}${parsed?.model ? ` (model ${parsed.model})` : ''}`,
      };
    }

    // Paper-record kind straight from the classifier, or nothing
    // conclusive — file it under the best guess.
    const docKind = (kind as DocumentKind | null) ?? nameKind;
    if (!MONEY_DOC_KINDS.has(docKind)) m.cost_cents = null;
    await fileToLibrary(propertyId, f.uri, docKind, label, mime, m);
    return {
      name, ok: true, where: 'files',
      detail: `Filed as ${DOCUMENT_KIND_LABEL[docKind].toLowerCase()}`,
    };
  };

  /** Run the batch pipeline over an arbitrary file list (picker or
   *  Android share sheet). */
  const runBatchOnFiles = async (
    files: { uri: string; name?: string | null; mimeType?: string | null; size?: number | null }[]
  ) => {
    const prop = await getActiveProperty(db);
    if (!prop) {
      void notice({
        title: 'No home set up yet',
        message: 'Finish onboarding first — files attach to a home.',
        tone: 'warning',
      });
      return;
    }
    setBatchResults(null);
    setPendingUri(null);
    const results: BatchOutcome[] = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const name = f.name || `File ${i + 1}`;
      setBatchProgress({ done: i, total: files.length, current: name });
      try {
        results.push(await batchOne(prop.id, f, name));
      } catch (err) {
        results.push({ name, ok: false, where: 'error', detail: String(err) });
      }
    }
    setBatchProgress(null);
    setBatchResults(results);
  };

  /** Batch Smart Scan — pick many files, auto-categorize each one. */
  const runBatch = async () => {
    let res: DocumentPicker.DocumentPickerResult;
    try {
      res = await DocumentPicker.getDocumentAsync({ multiple: true, copyToCacheDirectory: true });
    } catch (err) {
      void notice({ title: 'Picker failed', message: String(err), tone: 'error' });
      return;
    }
    if (res.canceled || res.assets.length === 0) return;
    await runBatchOnFiles(res.assets);
  };

  // Files shared into the app from another Android app (share sheet →
  // ShareIntentBridge stashes them and routes here) — consume once and
  // run them through the batch pipeline.
  useEffect(() => {
    const shared = takeSharedFiles();
    if (shared && shared.length > 0) void runBatchOnFiles(shared);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/(tabs)')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Smart scan</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 60 }} showsVerticalScrollIndicator={false}>
        <Text style={styles.h1}>Scan any home paper</Text>
        <Text style={styles.sub}>
          Receipt, invoice, estimate, appliance label, manual, warranty,
          policy, permit — snap it and the AI reads it, files it in the
          right place, and keeps the file.
        </Text>

        <Pressable
          onPress={pickScanSource}
          disabled={scanning}
          style={({ pressed }) => [
            styles.bigScan,
            pressed && { transform: [{ scale: 0.98 }] },
            scanning && { opacity: 0.7 },
          ]}>
          {scanning ? (
            <ActivityIndicator color={Palette.cream} />
          ) : (
            <ScanLine size={26} color={Palette.cream} strokeWidth={2} />
          )}
          <Text style={styles.bigScanText}>
            {scanning
              ? 'Reading the document…'
              : Platform.OS === 'web'
              ? 'Upload any file — photo, PDF, or document'
              : 'Snap a photo'}
          </Text>
          <Text style={styles.bigScanSub}>
            {scanning
              ? 'Vision is parsing the text — a couple of seconds.'
              : 'Estimates → Quotes · receipts → Expenses · labels → Inventory · records → Files'}
          </Text>
        </Pressable>

        {Platform.OS !== 'web' ? (
          <Pressable
            onPress={pickDocument}
            disabled={scanning || !!batchProgress}
            style={({ pressed }) => [styles.docBtn, pressed && { opacity: 0.85 }]}>
            <Upload size={16} color={Palette.forest} strokeWidth={2} />
            <Text style={styles.docBtnText}>Or upload a file — photo, PDF, or document</Text>
          </Pressable>
        ) : null}

        <Pressable
          onPress={() => void runBatch()}
          disabled={scanning || !!batchProgress}
          style={({ pressed }) => [styles.docBtn, pressed && { opacity: 0.85 }]}>
          <Layers size={16} color={Palette.forest} strokeWidth={2} />
          <Text style={styles.docBtnText}>Scan a whole stack — pick many files</Text>
        </Pressable>

        {batchProgress ? (
          <View style={styles.batchCard}>
            <View style={styles.batchHead}>
              <ActivityIndicator size="small" color={Palette.forest} />
              <Text style={styles.batchTitle}>
                Reading file {batchProgress.done + 1} of {batchProgress.total}…
              </Text>
            </View>
            <Text style={styles.batchCurrent} numberOfLines={1}>
              {batchProgress.current}
            </Text>
            <View style={styles.batchBarTrack}>
              <View
                style={[
                  styles.batchBarFill,
                  { width: `${Math.round((batchProgress.done / batchProgress.total) * 100)}%` },
                ]}
              />
            </View>
          </View>
        ) : null}

        {batchResults ? (
          <View style={styles.batchCard}>
            <View style={styles.batchHead}>
              <CheckCircle2 size={16} color={Palette.forest} strokeWidth={2} />
              <Text style={styles.batchTitle}>
                {batchResults.filter((r) => r.ok).length} of {batchResults.length} filed
                automatically
              </Text>
            </View>
            {batchResults.map((r, i) => (
              <View key={`${r.name}-${i}`} style={styles.batchRow}>
                <Text style={styles.batchRowName} numberOfLines={1}>
                  {r.name}
                </Text>
                <Text
                  style={[styles.batchRowDetail, !r.ok && { color: Palette.clay }]}
                  numberOfLines={2}>
                  {r.ok ? r.detail : `Couldn't process — ${r.detail}`}
                </Text>
              </View>
            ))}
            <View style={styles.pendingBtns}>
              {batchResults.some((r) => r.where === 'quote') ? (
                <Pressable
                  onPress={() => router.replace('/quotes' as never)}
                  style={({ pressed }) => [styles.pendingBtn, pressed && { opacity: 0.85 }]}>
                  <FileSearch size={14} color={Palette.cream} strokeWidth={2} />
                  <Text style={styles.pendingBtnText}>Quotes</Text>
                </Pressable>
              ) : null}
              {batchResults.some((r) => r.where === 'expense') ? (
                <Pressable
                  onPress={() => router.replace('/expenses' as never)}
                  style={({ pressed }) => [styles.pendingBtn, pressed && { opacity: 0.85 }]}>
                  <Receipt size={14} color={Palette.cream} strokeWidth={2} />
                  <Text style={styles.pendingBtnText}>Expenses</Text>
                </Pressable>
              ) : null}
              {batchResults.some((r) => r.where === 'inventory') ? (
                <Pressable
                  onPress={() => router.replace('/inventory' as never)}
                  style={({ pressed }) => [styles.pendingBtn, pressed && { opacity: 0.85 }]}>
                  <Package size={14} color={Palette.cream} strokeWidth={2} />
                  <Text style={styles.pendingBtnText}>Inventory</Text>
                </Pressable>
              ) : null}
              <Pressable
                onPress={() => router.replace('/media' as never)}
                style={({ pressed }) => [styles.pendingBtn, styles.pendingBtnAlt, pressed && { opacity: 0.85 }]}>
                <FileText size={14} color={Palette.forest} strokeWidth={2} />
                <Text style={[styles.pendingBtnText, { color: Palette.forest }]}>Files</Text>
              </Pressable>
            </View>
          </View>
        ) : null}

        <View style={styles.howRow}>
          <View style={styles.howCard}>
            <Receipt size={16} color={Palette.honeyDeep} strokeWidth={1.9} />
            <Text style={styles.howTitle}>Receipts & invoices</Text>
            <Text style={styles.howBody}>Filed as an expense — details pre-filled.</Text>
          </View>
          <View style={styles.howCard}>
            <FileSearch size={16} color={Palette.forest} strokeWidth={1.9} />
            <Text style={styles.howTitle}>Estimates & bids</Text>
            <Text style={styles.howBody}>Filed as a quote — compare, then convert.</Text>
          </View>
        </View>
        <View style={styles.howRow}>
          <View style={styles.howCard}>
            <Package size={16} color={Palette.honeyDeep} strokeWidth={1.9} />
            <Text style={styles.howTitle}>Appliance labels</Text>
            <Text style={styles.howBody}>Model + serial land in Inventory.</Text>
          </View>
          <View style={styles.howCard}>
            <FileText size={16} color={Palette.clay} strokeWidth={1.9} />
            <Text style={styles.howTitle}>Manuals & records</Text>
            <Text style={styles.howBody}>
              Warranties, policies, permits — auto-filed in Files.
            </Text>
          </View>
        </View>

        {pendingUri ? (
          <View style={styles.pendingCard}>
            <Image source={{ uri: pendingUri }} style={styles.pendingImage} resizeMode="cover" />
            <View style={styles.pendingHead}>
              <Sparkles size={14} color={Palette.honeyDeep} strokeWidth={2} />
              <Text style={styles.pendingTitle}>
                Couldn&apos;t auto-classify this one. Where should it go?
              </Text>
            </View>
            <View style={styles.pendingBtns}>
              <Pressable
                onPress={() => routeToForm('expense', pendingUri, pendingParsed)}
                style={({ pressed }) => [styles.pendingBtn, pressed && { opacity: 0.85 }]}>
                <Receipt size={14} color={Palette.cream} strokeWidth={2} />
                <Text style={styles.pendingBtnText}>Expense</Text>
              </Pressable>
              <Pressable
                onPress={() => routeToForm('quote', pendingUri, pendingParsed)}
                style={({ pressed }) => [styles.pendingBtn, pressed && { opacity: 0.85 }]}>
                <FileSearch size={14} color={Palette.cream} strokeWidth={2} />
                <Text style={styles.pendingBtnText}>Quote</Text>
              </Pressable>
            </View>
            <View style={styles.pendingBtns}>
              <Pressable
                onPress={() => routeToForm('inventory', pendingUri, pendingParsed)}
                style={({ pressed }) => [styles.pendingBtn, pressed && { opacity: 0.85 }]}>
                <Package size={14} color={Palette.cream} strokeWidth={2} />
                <Text style={styles.pendingBtnText}>Inventory</Text>
              </Pressable>
              <Pressable
                onPress={() =>
                  void fileAsDocument(pendingUri, 'photo', pendingParsed, 'image/jpeg', 'Scanned photo')
                }
                style={({ pressed }) => [styles.pendingBtn, styles.pendingBtnAlt, pressed && { opacity: 0.85 }]}>
                <FileText size={14} color={Palette.forest} strokeWidth={2} />
                <Text style={[styles.pendingBtnText, { color: Palette.forest }]}>Just save to Files</Text>
              </Pressable>
            </View>
          </View>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  bigScan: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    backgroundColor: Palette.forest,
    borderRadius: Radius.xl,
    paddingVertical: 28, paddingHorizontal: 20,
    alignItems: 'center', gap: 8,
  },
  bigScanText: {
    fontFamily: Fonts.sansBold, fontSize: 17, color: Palette.cream,
  },
  bigScanSub: {
    fontFamily: Fonts.sansMedium, fontSize: 12,
    color: 'rgba(251,246,234,0.75)', textAlign: 'center', lineHeight: 15,
  },
  docBtn: {
    marginTop: Spacing.two, marginHorizontal: Spacing.four,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8,
    borderWidth: 1.5, borderColor: Palette.forest,
    backgroundColor: Palette.card,
    borderRadius: 14, paddingVertical: 12,
  },
  docBtnText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest },
  howRow: {
    marginTop: Spacing.two, marginHorizontal: Spacing.four,
    flexDirection: 'row', gap: Spacing.two,
  },
  howCard: {
    flex: 1,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: 16, padding: 13, gap: 5,
  },
  howTitle: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  howBody: {
    fontFamily: Fonts.sansMedium, fontSize: 12, color: Palette.inkSoft,
    lineHeight: 15,
  },
  pendingCard: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: 14, gap: 10,
  },
  pendingImage: {
    height: 140, borderRadius: 12, backgroundColor: Palette.sandDeep,
  },
  pendingHead: { flexDirection: 'row', alignItems: 'center', gap: 7 },
  pendingTitle: {
    flex: 1,
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink, lineHeight: 18,
  },
  pendingBtns: { flexDirection: 'row', gap: 8 },
  pendingBtn: {
    flex: 1,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 7, backgroundColor: Palette.forest,
    paddingVertical: 12, borderRadius: 12,
  },
  pendingBtnAlt: {
    backgroundColor: Palette.card,
    borderWidth: 1.5, borderColor: Palette.forest,
  },
  pendingBtnText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.cream },
  batchCard: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: 14, gap: 8,
  },
  batchHead: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  batchTitle: { flex: 1, fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  batchCurrent: { fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft },
  batchBarTrack: {
    height: 6, borderRadius: 3, backgroundColor: Palette.sandDeep, overflow: 'hidden',
  },
  batchBarFill: { height: 6, borderRadius: 3, backgroundColor: Palette.forest },
  batchRow: {
    borderTopWidth: 1, borderTopColor: Palette.line, paddingTop: 8, gap: 2,
  },
  batchRowName: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink },
  batchRowDetail: { fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft, lineHeight: 15 },
});
